// The single resolver for a job type's DISPLAYED identity — its colour and the
// first letter of its name — across every surface that shows a type (badges,
// comboboxes, the board lens, pipeline cards, bank rows, closure tiles).
//
// A type's color used to be PURELY derived (getTypeColor hashes the id into a
// frozen palette). Now a dispatcher can CHOOSE a color (JobTemplate#color).
// Every call site holds only an ID, not the resource, so rather than thread
// the resource through ~15 places this module keeps a small reactive lookup —
// a derived-identity CACHE (same category as typeColors / formatters), not
// resource state — populated as a side effect of loading job templates.
//
// Contract:
//   - typeColor(id)   → the chosen color, else the deterministic hash. So an
//     org that never customizes a type sees EXACTLY today's colors, and every
//     existing spec/screen holds until a real chosen color lands.
//   - typeInitial(id) → the first letter of the type's NAME, uppercased, else
//     '' while the type is not registered yet (a disc with no letter).
//   - registerTypeIdentities(templates) MERGES, never replaces: a filtered /
//     paginated fetch returns a subset, and dropping the rest would blank other
//     types. Re-registering an id overwrites it (an edit propagates).
//   - Eventual consistency by design: a type shows the hash fallback (and no
//     letter) until some surface has loaded it, so first paint can flash
//     hash→chosen. Acceptable — the fallback is a valid color and self-corrects
//     reactively (typeColor reads reactive state, so computeds that call it
//     re-run when it fills).
//
// ⚰ The chosen ICON (JobTemplate#icon) is no longer read anywhere (design 169:
// the letter replaced the glyph). The column stays, unread, until a migration
// drops it — his call.
import { ref } from 'vue'
import type { components } from '@/api/schema'
import { getTypeColor } from '@/lib/typeColors'

type JobTemplate = components['schemas']['JobTemplateResource']

export interface TypeIdentity {
  color: string | null
  name: string
}

// id → chosen identity. Reassigned (not mutated in place) on every register so
// computeds depending on typeColor/typeInitial re-run.
const registry = ref<Record<string, TypeIdentity>>({})

export function registerTypeIdentities(templates: JobTemplate[]): void {
  if (!templates.length) return
  const next = { ...registry.value }
  for (const tpl of templates) {
    next[tpl.id] = {
      color: tpl.attributes.color ?? null,
      name: tpl.attributes.name ?? '',
    }
  }
  registry.value = next
}

// The chosen color if the type has one and it's been loaded, else the frozen
// deterministic hash (which also owns the null-id "Sans type" case).
export function typeColor(id?: string | null): string {
  if (id) {
    const chosen = registry.value[id]?.color
    if (chosen) return chosen
  }
  return getTypeColor(id)
}

// The first LETTER of a name (`\p{L}` — « — Taille » and « 2e passage » both
// find their letter), uppercased; '' when the name has none.
export function initialOf(name?: string | null): string {
  const letter = [ ...(name ?? '') ].find(char => /\p{L}/u.test(char))
  return letter ? letter.toUpperCase() : ''
}

// The registered type's initial, or '' until the type is known.
export function typeInitial(id?: string | null): string {
  if (!id) return ''
  return initialOf(registry.value[id]?.name)
}

// Test-only: clear the module singleton between specs so identities don't leak
// across tests (and so the hash-fallback assertions in typeColors/planningMap
// specs stay honest — an empty registry ⇒ typeColor === getTypeColor).
export function resetTypeIdentities(): void {
  registry.value = {}
}
